/*
    @providesModule SkillzCountingLabels

    usage:
    import {CashCountingLabel, ZCountingLabel, TicketzCountingLabel} from 'SkillzCountingLabels';

    ...
      <CashCountingLabel value={200}/>

    You may also add your own styles to override the defaults

      <ZCountingLabel style={{fontSize:36}} value={100}/>
*/
import {ELeagueMedal, LeagueMedal} from '@skillz/ui';
import AssetHelper from 'AssetHelper';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CountingLabel from 'CountingLabel';
import GlobalEventEmitter from 'GlobalEventEmitter';
import GradientLabel from 'GradientLabel';
import I18n from 'INTL';
import SKZConstants from 'SKZConstants';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Label from 'Label';
import React from 'react';
import {Animated, Platform, Pressable, StyleSheet, View} from 'react-native';
import {pickTextColorBasedOnBgColor} from '@skillz-rn/utils';

const {
  fontSizeMedium,
  fontSizeH2,
  fontSecondary,
  viewPrimaryColor,
  textColorCash,
  textColorPrimary,
  textColorTicketz,
  textColorZ,
  textCashGradient,
  textTicketzGradient,
  textZGradient,
  practiceCurrencyFullIconLargeURI,
} = SkillzStyle.fullStyles;

const buttonHeight = 2;
const DELAY_START_ANIMATION = 500;
const slopPadding = {
  top: 5,
  bottom: 5,
  left: 2,
  right: 2,
};

/* Z */
export class ZCountingLabel extends React.Component {
  static propTypes = {
    shouldAnimate: PropTypes.bool,
    style: PropTypes.any,
    value: PropTypes.number,
    coinLayout: PropTypes.func,
    coinRef: PropTypes.func,
    animationCurve: PropTypes.string,
    animationDuration: PropTypes.number,
    animationPrecision: PropTypes.number,
    identifier: PropTypes.string,
    forceAnimation: PropTypes.bool,
    containerStyle: PropTypes.any,
    iconSource: PropTypes.string,
    onPressFunc: PropTypes.func,
    padding: PropTypes.number,
  };

  static defaultProps = {
    shouldAnimate: true,
    animationPrecision: 0,
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
    iconSource: SkillzStyle.fullStyles.practiceCurrencyFullIconLargeURI,
  };

  _renderPreCoreLoopCountingLabel(): React.ReactElement {
    const style = this.props.style || {};
    const horizontalPadding = this.props.padding;
    return (
      <View style={[stylesV1.balanceItem, {paddingHorizontal: horizontalPadding}]}>
        <View style={stylesV1.iconFrame} onLayout={this.props.coinLayout} ref={this.props.coinRef}>
          <SkillzImage
            source={practiceCurrencyFullIconLargeURI}
            style={stylesV1.zImageStyle}
            resizeMode='contain'
          />
        </View>
        <View style={stylesV1.zFrame}>
          <CountingLabel
            identifier={'zBalance' + this.props.identifier}
            value={this.props.value}
            style={[stylesV1.zStyle, style]}
            formatter={(value) => I18n.getIntegerString(value)}
            animateDownwards={false}
            precision={0}
            animationCurve={this.props.animationCurve}
            animationDuration={this.props.animationDuration}
            animationPrecision={this.props.animationPrecision}
            countUpEnabled={this.props.shouldAnimate}
            forceAnimation={this.props.forceAnimation}
          />
        </View>
      </View>
    );
  }

  _renderPostCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};
    return (
      <View style={stylesV2.balanceItem}>
        <Pressable
          style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
          hitSlop={slopPadding}
          onPress={() => {
            if (this.props.onPressFunc) {
              this.props.onPressFunc();
            }
          }}
        >
          <View style={[stylesV2.frameContainer, this.props.containerStyle]}>
            <View
              style={stylesV2.iconFrame}
              onLayout={this.props.coinLayout}
              ref={this.props.coinRef}
            >
              <SkillzImage
                source={{uri: AssetHelper.pathForAssetNamed(this.props.iconSource)}}
                style={stylesV2.currencyCircleImage}
                resizeMode='contain'
              />
            </View>

            <CountingLabel
              identifier={'zBalance' + this.props.identifier}
              value={this.props.value}
              style={[stylesV2.zStyle, style]}
              formatter={(value) => I18n.abbreviatedNumber(value)}
              animateDownwards={false}
              precision={0}
              animationCurve={this.props.animationCurve}
              animationDuration={this.props.animationDuration}
              animationPrecision={this.props.animationPrecision}
              countUpEnabled={this.props.shouldAnimate}
              forceAnimation={this.props.forceAnimation}
            />
          </View>
        </Pressable>
      </View>
    );
  }

  render() {
    const PLAY_SCREEN_V2 =
      ClientPropertyConstants.isCoreLoopTungstenEnabled() ||
      ClientPropertyConstants.isCoreLoopTungstenLiteEnabled();
    if (PLAY_SCREEN_V2) {
      return this._renderPostCoreLoopCountingLabel();
    } else {
      return this._renderPreCoreLoopCountingLabel();
    }
  }
}

interface ZCountingLabelV2Props {
  shouldAnimate?: boolean;
  style?: any;
  value: number;
  coinLayout?: (event: any) => void;
  coinRef?: React.RefObject<View>;
  animationCurve?: string;
  animationDuration?: number;
  animationPrecision?: number;
  identifier?: string;
  forceAnimation?: boolean;
  containerStyle?: any;
  iconSource?: string;
  onPressFunc?: () => void;
  padding?: number;
  isCollapsible?: boolean;
}

export const ZCountingLabelV2: React.FC<ZCountingLabelV2Props> = ({
  shouldAnimate = true,
  style,
  value,
  coinLayout,
  coinRef,
  animationCurve = 'ease-in',
  animationDuration = 2000,
  animationPrecision = 0,
  identifier = '',
  forceAnimation = false,
  containerStyle,
  iconSource = SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI,
  onPressFunc,
  padding,
  isCollapsible = false,
}) => {
  const renderPreCoreLoopCountingLabel = () => {
    const horizontalPadding = padding;
    return (
      <View style={[stylesV1.balanceItem, {paddingHorizontal: horizontalPadding}]}>
        <View style={stylesV1.iconFrame} onLayout={coinLayout} ref={coinRef}>
          <SkillzImage source={iconSource} style={stylesV1.zImageStyle} resizeMode='contain' />
        </View>
        <View style={stylesV1.zFrame}>
          <CountingLabel
            identifier={`zBalance${identifier}`}
            value={value}
            style={[stylesV1.zStyle, style]}
            formatter={(val) => I18n.getIntegerString(val)}
            animateDownwards={false}
            precision={0}
            animationCurve={animationCurve}
            animationDuration={animationDuration}
            animationPrecision={animationPrecision}
            countUpEnabled={shouldAnimate}
            forceAnimation={forceAnimation}
          />
        </View>
      </View>
    );
  };

  const renderPostCoreLoopCountingLabel = () => {
    const flattenedStyle = StyleSheet.flatten(style) || {};
    const tintColorForTheme = pickTextColorBasedOnBgColor(
      stylesV2.cashContainer.backgroundColor as string,
      '#E2AC37',
      '#23BA5D'
    );

    return (
      <View style={stylesV2.balanceItem}>
        <Pressable
          style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
          hitSlop={{top: 5, bottom: 5, left: 2, right: 2}}
          onPress={() => {
            if (onPressFunc) {
              onPressFunc();
            }
          }}
        >
          <View
            style={[
              stylesV2.frameContainer,
              containerStyle,
              {paddingRight: isCollapsible ? 0 : 12},
            ]}
            ref={coinRef}
          >
            {isCollapsible ? (
              <>
                <SkillzImage
                  source={iconSource}
                  style={[stylesV2.currencyCircleImage]}
                  resizeMode='contain'
                />
                <View>
                  <SkillzImage
                    source={'ic_new_tooltip_circle.svg'}
                    style={[
                      stylesV2.currencyCircleImage,
                      {
                        position: 'absolute',
                        height: 16,
                        width: 16,
                        right: -4,
                        bottom: -18,
                      },
                    ]}
                    resizeMode='contain'
                    tintColor={tintColorForTheme}
                  />
                  <SkillzImage
                    source={'ic_new_tooltip_i.svg'}
                    style={[
                      stylesV2.currencyCircleImage,
                      {
                        position: 'absolute',
                        height: 16,
                        width: 16,
                        right: -4,
                        bottom: -18,
                      },
                    ]}
                    resizeMode='contain'
                  />
                </View>
              </>
            ) : (
              <View style={stylesV2.iconFrame} onLayout={coinLayout} ref={coinRef}>
                <SkillzImage
                  source={iconSource}
                  style={[stylesV2.currencyCircleImage]}
                  resizeMode='contain'
                />
              </View>
            )}
            {!isCollapsible && (
              <CountingLabel
                identifier={`zBalance${identifier}`}
                value={value}
                style={[stylesV2.zStyle, flattenedStyle, isCollapsible ? {paddingLeft: 4} : {}]}
                formatter={(val) => I18n.abbreviatedNumber(val)}
                animateDownwards={false}
                precision={0}
                animationCurve={animationCurve}
                animationDuration={animationDuration}
                animationPrecision={animationPrecision}
                countUpEnabled={shouldAnimate}
                forceAnimation={forceAnimation}
              />
            )}
          </View>
        </Pressable>
      </View>
    );
  };

  const PLAY_SCREEN_V2 =
    ClientPropertyConstants.isCoreLoopTungstenEnabled() ||
    ClientPropertyConstants.isCoreLoopTungstenLiteEnabled();

  return PLAY_SCREEN_V2 ? renderPostCoreLoopCountingLabel() : renderPreCoreLoopCountingLabel();
};

interface AddCashButtonProps {
  style?: any;
  value: string;
  containerStyle?: any;
  onPressFunc?: () => void;
}

export const AddCashButton: React.FC<AddCashButtonProps> = ({
  style,
  value,
  containerStyle,
  onPressFunc,
}) => {
  const flattenedStyle = StyleSheet.flatten(style) || {};

  return (
    <View style={stylesV2.balanceItem}>
      <Pressable
        style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
        hitSlop={slopPadding}
        onPress={() => {
          if (onPressFunc) {
            onPressFunc();
          }
        }}
      >
        <View style={[stylesV2.frameContainer, containerStyle]}>
          <SkillzImage
            source={'ic_add_cash.svg'}
            style={[stylesV2.currencyCircleImage]}
            resizeMode='contain'
          />
          <Label style={[stylesV2.zStyle, flattenedStyle, {paddingLeft: 4}]}>{value}</Label>
        </View>
      </Pressable>
    </View>
  );
};
/* Cash */
export class CashCountingLabel extends React.Component {
  static propTypes = {
    shouldAnimate: PropTypes.bool,
    style: PropTypes.any,
    value: PropTypes.number,
    cashLayout: PropTypes.func,
    cashRef: PropTypes.func,
    animationCurve: PropTypes.string,
    animationDuration: PropTypes.number,
    animationPrecision: PropTypes.number,
    identifier: PropTypes.string,
    forceAnimation: PropTypes.bool,
    containerStyle: PropTypes.any,
    onPressFunc: PropTypes.func,
    padding: PropTypes.number,
  };

  static defaultProps = {
    shouldAnimate: true,
    animationPrecision: 2,
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
    containerStyle: {},
  };

  _renderPreCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};
    const horizontalPadding = this.props.padding;
    return (
      <View style={[stylesV1.balanceItem, {paddingHorizontal: horizontalPadding}]}>
        <View style={stylesV1.iconFrame} onLayout={this.props.cashLayout} ref={this.props.cashRef}>
          <GradientLabel
            style={[stylesV1.cashStyle, style, {textAlign: 'right'}]}
            numberOfLines={1}
          >
            {I18n.currencySymbol()}
          </GradientLabel>
        </View>
        <CountingLabel
          identifier={'cashBalance' + this.props.identifier}
          value={this.props.value}
          style={[stylesV1.cashStyle, style]}
          formatter={(value) => I18n.getCurrencyString(value, '', false, false)}
          animateDownwards={false}
          precision={2}
          animationCurve={this.props.animationCurve}
          animationDuration={this.props.animationDuration}
          animationPrecision={this.props.animationPrecision}
          countUpEnabled={this.props.shouldAnimate}
          forceAnimation={this.props.forceAnimation}
        />
      </View>
    );
  }

  _renderPostCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};

    return (
      <View style={stylesV2.balanceItem}>
        <Pressable
          style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
          hitSlop={slopPadding}
          onPress={() => {
            if (this.props.onPressFunc) {
              this.props.onPressFunc();
            }
          }}
        >
          <View style={[stylesV2.cashContainer, this.props.containerStyle]}>
            <View
              style={stylesV2.iconFrame}
              onLayout={this.props.cashLayout}
              ref={this.props.cashRef}
            >
              <SkillzImage
                source={{uri: AssetHelper.pathForAssetNamed('ic_cash_usd_background.png')}}
                style={stylesV2.currencyCircleImage}
                resizeMode='contain'
              />
            </View>
            <CountingLabel
              identifier={'cashBalance' + this.props.identifier}
              value={this.props.value}
              style={[stylesV2.cashStyle, style]}
              formatter={(value) => I18n.getCurrencyString(value, '', false, false)}
              animateDownwards={false}
              precision={2}
              animationCurve={this.props.animationCurve}
              animationDuration={this.props.animationDuration}
              animationPrecision={this.props.animationPrecision}
              countUpEnabled={this.props.shouldAnimate}
              forceAnimation={this.props.forceAnimation}
            />
          </View>
        </Pressable>
      </View>
    );
  }

  render() {
    const PLAY_SCREEN_V2 =
      ClientPropertyConstants.isCoreLoopTungstenEnabled() ||
      ClientPropertyConstants.isCoreLoopTungstenLiteEnabled();
    if (PLAY_SCREEN_V2) {
      return this._renderPostCoreLoopCountingLabel();
    } else {
      return this._renderPreCoreLoopCountingLabel();
    }
  }
}

// Wallet Balance

interface WalletCountingLabelProps {
  shouldAnimate?: boolean;
  style?: any;
  value: number;
  cashLayout?: (event: any) => void;
  cashRef?: React.RefObject<View>;
  animationCurve?: string;
  animationDuration?: number;
  animationPrecision?: number;
  identifier?: string;
  forceAnimation?: boolean;
  containerStyle?: any;
  onPressFunc?: () => void;
  padding?: number;
}

export const WalletCountingLabel: React.FC<WalletCountingLabelProps> = ({
  shouldAnimate = true,
  style,
  value,
  cashLayout,
  cashRef,
  animationCurve = 'ease-in',
  animationDuration = 2000,
  animationPrecision = 2,
  identifier = '',
  forceAnimation = false,
  containerStyle,
  onPressFunc,
}) => {
  const flattenedStyle = StyleSheet.flatten(style) || {};

  return (
    <View style={stylesV2.balanceItem}>
      <Pressable
        style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
        hitSlop={slopPadding}
        onPress={() => {
          if (onPressFunc) {
            onPressFunc();
          }
        }}
      >
        <View style={[stylesV2.cashContainer, containerStyle]}>
          <View style={stylesV2.iconFrame} onLayout={cashLayout} ref={cashRef}>
            <SkillzImage
              source={'ic_new_wallet_v2.svg'}
              style={stylesV2.currencyCircleImage}
              resizeMode='contain'
            />
          </View>
          <CountingLabel
            identifier={`cashBalance${identifier}`}
            value={value}
            style={[stylesV2.cashStyle, flattenedStyle]}
            formatter={(val) => I18n.getCurrencyString(val, '', false, false)}
            animateDownwards={false}
            precision={animationPrecision}
            animationCurve={animationCurve}
            animationDuration={animationDuration}
            animationPrecision={animationPrecision}
            countUpEnabled={shouldAnimate}
            forceAnimation={forceAnimation}
          />
        </View>
      </Pressable>
    </View>
  );
};

/* Ticketz */
export class TicketzCountingLabel extends React.Component {
  static propTypes = {
    shouldAnimate: PropTypes.bool,
    style: PropTypes.any,
    value: PropTypes.number,
    ticketzRef: PropTypes.func,
    ticketzLayout: PropTypes.func,
    ticketzIconSource: PropTypes.oneOf(['ic_ticketz_background.png']),
    animationCurve: PropTypes.string,
    animationDuration: PropTypes.number,
    animationPrecision: PropTypes.number,
    identifier: PropTypes.string,
    forceAnimation: PropTypes.bool,
    containerStyle: PropTypes.any,
    onPressFunc: PropTypes.func,
  };

  static defaultProps = {
    shouldAnimate: true,
    animationPrecision: 0,
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
  };

  _renderPreCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};
    const {justifyContent = 'flex-start', marginLeft = 0} = style;
    return (
      <View
        style={{
          justifyContent,
          marginLeft,
          flexDirection: 'row',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <View ref={this.props.ticketzRef} onLayout={this.props.ticketzLayout}>
          <SkillzImage
            source={'ic_ticketz_background.png'}
            style={stylesV1.ticketzImageStyle}
            resizeMode='contain'
          />
        </View>
        <View>
          <CountingLabel
            identifier={'ticketzBalance' + this.props.identifier}
            value={this.props.value}
            style={[stylesV1.ticketzStyle, style]}
            formatter={(value) => I18n.abbreviatedNumber(value)}
            animateDownwards={false}
            animationCurve={this.props.animationCurve}
            animationDuration={this.props.animationDuration}
            animationPrecision={this.props.animationPrecision}
            countUpEnabled={this.props.shouldAnimate}
            forceAnimation={this.props.forceAnimation}
          />
        </View>
      </View>
    );
  }

  _renderPostCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};
    return (
      <View style={stylesV2.balanceItem}>
        <Pressable
          style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
          hitSlop={slopPadding}
          onPress={() => {
            if (this.props.onPressFunc) {
              this.props.onPressFunc();
            }
          }}
        >
          <View style={[stylesV2.frameContainer, this.props.containerStyle]}>
            <View
              style={stylesV2.iconFrame}
              onLayout={this.props.ticketzLayout}
              ref={this.props.ticketzRef}
            >
              <SkillzImage
                source={{uri: AssetHelper.pathForAssetNamed('ic_ticketz_background.png')}}
                style={stylesV2.currencyCircleImage}
                resizeMode='contain'
              />
            </View>
            <CountingLabel
              identifier={'ticketzBalance' + this.props.identifier}
              value={this.props.value}
              style={[stylesV2.ticketzStyle, style]}
              formatter={(value) => I18n.abbreviatedNumber(value)}
              animateDownwards={false}
              animationCurve={this.props.animationCurve}
              animationDuration={this.props.animationDuration}
              animationPrecision={this.props.animationPrecision}
              countUpEnabled={this.props.shouldAnimate}
              forceAnimation={this.props.forceAnimation}
            />
          </View>
        </Pressable>
      </View>
    );
  }

  render() {
    const PLAY_SCREEN_V2 =
      ClientPropertyConstants.isCoreLoopTungstenEnabled() ||
      ClientPropertyConstants.isCoreLoopTungstenLiteEnabled();
    if (PLAY_SCREEN_V2) {
      return this._renderPostCoreLoopCountingLabel();
    } else {
      return this._renderPreCoreLoopCountingLabel();
    }
  }
}

/* Medals */
export class MedalCountingLabel extends React.Component {
  static propTypes = {
    shouldAnimate: PropTypes.bool,
    style: PropTypes.any,
    value: PropTypes.number,
    medalRef: PropTypes.func,
    medalLayout: PropTypes.func,
    animationCurve: PropTypes.string,
    animationDuration: PropTypes.number,
    animationPrecision: PropTypes.number,
    identifier: PropTypes.string,
    forceAnimation: PropTypes.bool,
    containerStyle: PropTypes.any,
    onPressFunc: PropTypes.func,
  };

  static defaultProps = {
    shouldAnimate: true,
    animationPrecision: 0,
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
  };

  _renderPreCoreLoopCountingLabel() {
    const size = Sizes.select({
      landscapePhone: 12,
      portraitPhone: 20,
      portraitTablet: 21,
      landscapeTablet: 30,
    });

    const style = StyleSheet.flatten(this.props.style) || {};
    const {justifyContent = 'flex-start', marginLeft = 0} = style;
    return (
      <View
        style={{
          justifyContent,
          marginLeft,
          flexDirection: 'row',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <View ref={this.props.medalRef} onLayout={this.props.medalLayout}>
          <LeagueMedal medal={ELeagueMedal.UNIFIED} styleOverwrite={{size: size}} />
        </View>
        <View>
          <CountingLabel
            identifier={'medalBalance' + this.props.identifier}
            value={this.props.value}
            style={[stylesV1.medalStyle, style]}
            formatter={(value) => I18n.abbreviatedNumber(value)}
            animateDownwards={false}
            animationCurve={this.props.animationCurve}
            animationDuration={this.props.animationDuration}
            animationPrecision={this.props.animationPrecision}
            countUpEnabled={this.props.shouldAnimate}
            forceAnimation={this.props.forceAnimation}
          />
        </View>
      </View>
    );
  }

  _renderPostCoreLoopCountingLabel() {
    const style = StyleSheet.flatten(this.props.style) || {};
    return (
      <View style={stylesV2.balanceItem}>
        <Pressable
          style={({pressed}) => [pressed ? stylesV2.pressed : {}]}
          hitSlop={slopPadding}
          onPress={() => {
            if (this.props.onPressFunc) {
              this.props.onPressFunc();
            }
          }}
        >
          <View style={[stylesV2.frameContainer, this.props.containerStyle]}>
            <View
              style={[stylesV2.iconFrame, stylesV2.medalIconBorder]}
              onLayout={this.props.medalLayout}
              ref={this.props.medalRef}
            >
              <LeagueMedal medal={ELeagueMedal.UNIFIED} styleOverwrite={{size: 22}} />
            </View>
            <CountingLabel
              identifier={'medalBalance' + this.props.identifier}
              value={this.props.value}
              style={[stylesV2.medalStyle, style]}
              formatter={(value) => I18n.abbreviatedNumber(value)}
              animateDownwards={false}
              animationCurve={this.props.animationCurve}
              animationDuration={this.props.animationDuration}
              animationPrecision={this.props.animationPrecision}
              countUpEnabled={this.props.shouldAnimate}
              forceAnimation={this.props.forceAnimation}
            />
          </View>
        </Pressable>
      </View>
    );
  }

  render() {
    const PLAY_SCREEN_V2 =
      ClientPropertyConstants.isCoreLoopTungstenEnabled() ||
      ClientPropertyConstants.isCoreLoopTungstenLiteEnabled();
    if (PLAY_SCREEN_V2) {
      return this._renderPostCoreLoopCountingLabel();
    } else {
      return this._renderPreCoreLoopCountingLabel();
    }
  }
}

const stylesV1 = StyleSheet.create({
  balanceItem: {
    alignSelf: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    flex: Sizes.select({
      portraitTablet: 0,
      other: 1,
    }),
  },

  cashStyle: {
    ...fontSecondary,
    ...fontSizeH2,
    ...Platform.select({
      android: {...textColorCash},
      ios: {gradientColors: textCashGradient},
    }),
    ...Sizes.select({
      portraitTablet: fontSizeMedium,
      other: {},
    }),
  },

  zStyle: {
    ...Platform.select({
      ios: {gradientColors: textZGradient},
      android: {...textColorZ},
    }),
    ...fontSecondary,
    ...fontSizeH2,
    ...Sizes.select({
      tablet: {
        left: 3,
      },
      other: {},
    }),
  },

  zImageStyle: {
    marginTop: 3,
    alignSelf: 'center',
    ...Sizes.select({
      landscapePhone: {
        height: 12,
        width: 12,
      },
      portraitPhone: {
        height: 20,
        width: 20,
        left: 0,
        bottom: 1,
      },
      portraitTablet: {
        height: 21,
        width: 21,
      },
      landscapeTablet: {
        height: 30,
        width: 30,
      },
    }),
  },

  ticketzStyle: {
    ...Platform.select({
      ios: {gradientColors: textTicketzGradient},
      android: {...textColorTicketz},
    }),
    ...fontSecondary,
    ...fontSizeH2,
    ...Sizes.select({
      portraitTablet: fontSizeMedium,
      other: {},
    }),
  },

  medalStyle: {
    ...Platform.select({
      ios: {gradientColors: ['#9a9a9a', '#9a9a9a']},
      android: {color: '#9a9a9a'},
    }),
    ...fontSecondary,
    ...fontSizeH2,
    ...Sizes.select({
      portraitTablet: fontSizeMedium,
      other: {},
    }),
  },

  zFrame: {
    justifyContent: 'center',
  },

  iconFrame: {
    position: 'relative',
    alignSelf: 'center',
    justifyContent: Sizes.select({phone: 'center', tablet: 'flex-end'}),
  },

  ticketzImageStyle: {
    alignSelf: 'center',
    ...Sizes.select({
      landscapePhone: {
        height: 12,
        width: 12,
      },
      portraitTablet: {
        height: 20,
        width: 21,
      },
      other: Sizes.dynamic({
        sm: {
          height: 18,
          width: 24,
        },
        xxlg: {
          height: 30,
          width: 40,
        },
      }),
    }),
  },
});

const stylesV2 = StyleSheet.create({
  balanceItem: {
    alignSelf: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },

  pressed: {
    opacity: 0.5,
  },

  cashStyle: {
    fontFamily: 'OpenSans-Bold',
    ...textColorPrimary,
    ...Sizes.select({
      phone: {
        paddingLeft: 32,
        fontSize: 14,
      },
      tablet: {
        paddingLeft: 40,
        fontSize: 20,
      },
    }),
  },

  currencyCircleImage: {
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
    ...Sizes.select({
      phone: {
        height: 28,
        width: 28,
      },
      tablet: {
        height: 34,
        width: 34,
      },
    }),
  },

  cashContainer: {
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 25,
    paddingRight: 12,
    borderColor: SkillzStyle.convertToOpacity(
      SkillzStyle.useValueForTheme('#000000', '#FFFFFF'),
      SkillzStyle.useValueForTheme(0.15, 0.2)
    ),
    backgroundColor: SkillzStyle.convertToOpacity(
      SkillzStyle.useValueForTheme('#DDDDDD', '#000000'),
      SkillzStyle.useValueForTheme(0.2, 0.22)
    ),
    ...Sizes.select({
      phone: {
        height: 30,
        maxHeight: 30,
        ...Platform.select({
          ios: {
            maxWidth: 110,
          },
          android: {
            maxWidth: 105,
          },
        }),
      },
      tablet: {
        height: 38,
        maxHeight: 38,
      },
    }),
  },

  zStyle: {
    ...textColorPrimary,
    fontFamily: 'OpenSans-Bold',
    ...Sizes.select({
      phone: {
        paddingLeft: 32,
        fontSize: 14,
      },
      tablet: {
        paddingLeft: 40,
        fontSize: 20,
      },
    }),
  },

  ticketzStyle: {
    ...textColorPrimary,
    fontFamily: 'OpenSans-Bold',
    ...Sizes.select({
      phone: {
        paddingLeft: 32,
        fontSize: 14,
      },
      tablet: {
        paddingLeft: 40,
        fontSize: 20,
      },
    }),
  },

  medalStyle: {
    ...textColorPrimary,
    fontFamily: 'OpenSans-Bold',
    fontSize: 14,
    ...Sizes.select({
      phone: {
        paddingLeft: 32,
        fontSize: 14,
      },
      tablet: {
        paddingLeft: 40,
        fontSize: 20,
      },
    }),
  },

  frameContainer: {
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 25,
    borderColor: SkillzStyle.convertToOpacity(
      SkillzStyle.useValueForTheme('#000000', '#FFFFFF'),
      SkillzStyle.useValueForTheme(0.15, 0.2)
    ),
    paddingRight: 12,
    backgroundColor: SkillzStyle.convertToOpacity(
      SkillzStyle.useValueForTheme('#DDDDDD', '#000000'),
      SkillzStyle.useValueForTheme(0.2, 0.22)
    ),
    ...Sizes.select({
      phone: {
        height: 30,
        maxHeight: 30,
        maxWidth: 110,
      },
      tablet: {
        height: 38,
        maxHeight: 38,
      },
    }),
  },

  iconFrame: {
    position: 'absolute',
    alignSelf: 'center',
    justifyContent: Sizes.select({phone: 'center', tablet: 'flex-end'}),
    overflow: 'visible',
  },

  medalIconBorder: {
    borderRightWidth: 2,
    borderBottomWidth: 1,
    borderColor: '#00000000',
  },
});
